<div class="opportunity-view">
  @if (opportunity$ | async; as opp) {
    <ng-container
      [ngTemplateOutlet]="opportunityTmpl"
      [ngTemplateOutletContext]="{ opp }"
    ></ng-container>
  } @else {
    <app-loader></app-loader>
  }
</div>

<ng-template #opportunityTmpl let-opp="opp">
  @if (vmExperts$ | async; as vmE) {
    @if (vmE.loading) {
      <app-loader></app-loader>
    } @else {
      <div class="top-section-container">
        <div class="description-div flex flex-col gap-2">
          <div
            class="flex flex-col justify-between content-center gap-2 gt-sm:flex-row gt-sm:gap-0"
          >
            <h2 data-test-id="opportunity-welcome-header">
              Welcome to your project portal for {{ opp.opportunityName }}
            </h2>
            <div
              class="flex flex-col gap-2 justify-center content-center gt-sm:flex-row"
            >
              <a
                mat-stroked-button
                color="primary"
                class="icon-button primaryColor-border"
                data-test-id="opportunity-settings"
                queryParamsHandling="preserve"
                [routerLink]="['/connect', opp.publicDisplayId, 'settings']"
              >
                <mat-icon>settings</mat-icon>
                <span data-test-id="opportunity-settings-label">
                  Project settings
                </span>
              </a>
              @if (!['Self-Serve', 'RFQ'].includes(opp.stageName)) {
                <button
                  mat-stroked-button
                  color="primary"
                  class="icon-button primaryColor-border"
                  matTooltipPosition="left"
                  data-test-id="opportunity-export-profiles"
                  matTooltip="Download all profiles"
                  [appExperts]="vmE.experts"
                  [opportunityName]="opp.opportunityName"
                >
                  <mat-icon>ic_file_download</mat-icon>
                  <span data-test-id="opportunity-export-profiles-label">
                    Export profiles
                  </span>
                </button>
                @if (opp.screenerComparisonEnabled) {
                  <button
                    mat-flat-button
                    color="primary"
                    class="icon-button primaryColor-border"
                    data-test-id="opportunity-compare-screeners"
                    matTooltip="Compare profile screeners"
                    (click)="navigateScreener(opp.opportunityId)"
                  >
                    <mat-icon class="secondaryColor">compare_arrows</mat-icon>
                    <span data-test-id="opportunity-compare-screeners-label">
                      Compare screeners
                    </span>
                  </button>
                }
              } @else {
                <button
                  mat-flat-button
                  color="primary"
                  class="icon-button primaryColor-border"
                  data-test-id="opportunity-request-recruitment"
                  (click)="requestRecruitment(opp)"
                >
                  <mat-icon class="secondaryColor">group_search</mat-icon>
                  <span data-test-id="opportunity-request-recruitment-label">
                    Get recruitment support
                  </span>
                </button>
              }
            </div>
          </div>
          @if (vmE.expertApprovalRequired) {
            <div>
              <ng-container
                [ngTemplateOutlet]="approvalRequiredBannerTmpl"
              ></ng-container>
            </div>
          }
        </div>
        <mat-tab-group
          class="main-tab"
          color="primary"
          id="tab-group"
          mat-stretch-tabs
          data-test-id="opportunity-header-group"
          [(selectedIndex)]="selectedIndex"
          (selectedTabChange)="selectedTabChange($event.index)"
        >
          @if (!['Self-Serve', 'RFQ'].includes(opp.stageName)) {
            <mat-tab>
              <ng-template mat-tab-label>
                <span data-test-id="opportunity-project-dashboard-tab">
                  Project Dashboard
                </span>
              </ng-template>
              <div
                class="project-progress flex flex-col items-start gt-xs:flex-row"
              >
                <app-notifications
                  [opportunityId]="opp.opportunityId"
                ></app-notifications>
                <app-geographic-progress
                  class="flex flex-col flex-grow"
                  [opportunity]="opp"
                  (adjustTabIndex)="adjustTabIndex($event)"
                  (openOmnisearch)="openOmnisearchFromProjectProgressPage()"
                >
                </app-geographic-progress>
              </div>
            </mat-tab>
            @if (vmE.experts.newExperts; as newExperts) {
              <mat-tab>
                <ng-template mat-tab-label>
                  <span data-test-id="opportunity-new-profiles-tab">
                    New Profiles
                  </span>
                  @if (newExperts.length) {
                    <span
                      class="count secondaryColorBG primaryColor"
                      data-test-id="opportunity-new-profiles-count"
                    >
                      {{ newExperts.length }}
                    </span>
                  }
                </ng-template>
                <app-expert-list
                  viewStage="new"
                  [expertLoadingIds]="expertLoadingIds$ | async"
                  [experts]="newExperts"
                  [opportunity]="opp"
                  [isExpertApprovalRequired]="vmE.expertApprovalRequired"
                >
                </app-expert-list>
              </mat-tab>
            }
            @if (vmE.experts.acceptedExperts; as acceptedExperts) {
              <mat-tab>
                <ng-template mat-tab-label>
                  <span data-test-id="opportunity-upcoming-connections-tab">
                    Upcoming Connections
                  </span>
                  @if (acceptedExperts.length) {
                    <span
                      class="count secondaryColorBG primaryColor"
                      data-test-id="opportunity-upcoming-connections-count"
                    >
                      {{ acceptedExperts.length }}
                    </span>
                  }
                </ng-template>
                <div class="accepted-experts">
                  <app-expert-list
                    viewStage="accepted"
                    [expertLoadingIds]="expertLoadingIds$ | async"
                    [experts]="acceptedExperts"
                    [opportunity]="opp"
                    [isExpertApprovalRequired]="vmE.expertApprovalRequired"
                  >
                  </app-expert-list>
                </div>
              </mat-tab>
            }
            @if (vmE.experts.completedConnections; as completedConnections) {
              <mat-tab>
                <ng-template mat-tab-label>
                  <span data-test-id="opportunity-completed-connections-tab">
                    Completed Connections
                  </span>
                  @if (completedConnections.length) {
                    <span
                      class="count secondaryColorBG primaryColor"
                      data-test-id="opportunity-completed-connections-count"
                    >
                      {{ completedConnections.length }}
                    </span>
                  }
                </ng-template>
                <div class="accepted-experts">
                  <app-expert-list
                    viewStage="completed"
                    [expertLoadingIds]="expertLoadingIds$ | async"
                    [experts]="completedConnections"
                    [opportunity]="opp"
                    [isExpertApprovalRequired]="vmE.expertApprovalRequired"
                  >
                  </app-expert-list>
                </div>
              </mat-tab>
            }
            @if (vmE.experts.declinedExperts; as declinedExperts) {
              <mat-tab [disabled]="!canViewRejectedExperts">
                <ng-template mat-tab-label>
                  <span data-test-id="opportunity-rejected-profiles-tab">
                    Rejected Profiles
                  </span>
                  @if (declinedExperts.length) {
                    <span
                      class="count secondaryColorBG primaryColor"
                      data-test-id="opportunity-rejected-profiles-count"
                    >
                      {{ declinedExperts.length }}
                    </span>
                  }
                </ng-template>
                <div class="declined-experts">
                  <app-expert-list
                    viewStage="declined"
                    [expertLoadingIds]="expertLoadingIds$ | async"
                    [experts]="declinedExperts"
                    [opportunity]="opp"
                    [isExpertApprovalRequired]="vmE.expertApprovalRequired"
                  >
                  </app-expert-list>
                </div>
              </mat-tab>
            }
          }
          <mat-tab [disabled]="!showDataCapture">
            <ng-template mat-tab-label>
              <span
                class="flex flex-row items-center gap-2"
                data-test-id="opportunity-data-capture-tab"
                matTooltip="Contact your project manager to gain access to Techspert's Data Capture"
                [matTooltipDisabled]="!canAccessDataCapture || showDataCapture"
              >
                <mat-icon
                  class="data-capture-icon"
                  matBadge="!"
                  matBadgeSize="small"
                  matBadgeColor="warn"
                  matTooltip="Please set up your first Data Capture sheet"
                  [matBadgeHidden]="
                    !(showDataCaptureSheetNotification$ | async)
                      ?.showNotification
                  "
                  [matTooltipDisabled]="
                    !(showDataCaptureSheetNotification$ | async)
                      ?.showNotification
                  "
                >
                  table_view
                </mat-icon>
                Data Capture
              </span>
            </ng-template>
            @if (opp.dataCapture.termsAndConditionsRequired) {
              <div
                class="tab-container-outline flex flex-col content-center justify-center gap-16 h-full text-center"
              >
                <div class="tab-container-max">
                  <div class="content-center justify-center gap-16">
                    <h2 data-test-id="opportunity-set-up-data-capture-title">
                      Set up your Data Capture in 5 easy steps
                    </h2>
                    <div
                      class="mb-6"
                      data-test-id="opportunity-accept-terms-and-conditions-section"
                    >
                      <mat-checkbox
                        data-test-id="opportunity-terms-and-conditions-checkbox"
                        [formControl]="dataCaptureTermsAndConditions"
                      >
                        <span
                          [class.error]="
                            dataCaptureTermsAndConditions.touched &&
                            dataCaptureTermsAndConditions.invalid
                          "
                        >
                          I agree to abide by Techspert's
                          <a
                            href="https://techspert.com/dc-terms"
                            target="_blank"
                            data-test-id="opportunity-data-capture-terms-and-conditions-link"
                          >
                            Terms and Conditions
                          </a>
                        </span>
                      </mat-checkbox>
                    </div>
                    <button
                      mat-flat-button
                      color="primary"
                      data-test-id="opportunity-accept-terms-and-conditions-button"
                      (click)="
                        acceptDataCaptureTermsAndConditions(
                          opp.clientId,
                          opp.opportunityId
                        )
                      "
                    >
                      Get started
                    </button>
                  </div>
                </div>
              </div>
            }
            @if (opp.dataCapture.enableCapture) {
              <app-data-capture
                [opportunityId]="opp.opportunityId"
                [publicDisplayId]="opp.publicDisplayId"
              ></app-data-capture>
            }
          </mat-tab>
        </mat-tab-group>
      </div>
    }
  } @else {
    <app-loader></app-loader>
  }
</ng-template>

<ng-template #approvalRequiredBannerTmpl>
  <div
    class="warning-box"
    data-test-id="opportunity-compliance-approval-required-banner"
  >
    <div class="flex gap-2">
      <mat-icon>lock</mat-icon>
      <div class="flex flex-col gap-2">
        <b data-test-id="opportunity-compliance-approval-required-title"
          >Compliance approval required</b
        >
        <p data-test-id="opportunity-compliance-approval-required-message">
          All experts in this project require approval prior to scheduling for
          compliance reasons.
        </p>
      </div>
    </div>
  </div>
</ng-template>
